import { Button, Card, Form } from '../../../ui';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Spinner } from '../../../ui';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useServiceErrorHandler } from '../../../../hooks/useServiceErrorHandler';
import { reportError, useSession } from '../../../../state/app-state';
import * as slackManager from '../../../../services/slack';
import { cancelSlackAuth } from '../../../../services/slack';

const SlackCallbackPage: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();

  const handleServiceError = useServiceErrorHandler();
  const session = useSession();

  const [failed, setFailed] = useState(false);
  const [isAddingSlackToBrief, setIsAddingSlackToBrief] = useState<boolean>();
  const [briefAdded, setBriefAdded] = useState<boolean>();
  const [code, setCode] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);

  const closeWindow = useCallback(() => {
    if (window.opener && window.opener.popupCompleted) {
      window.opener.popupCompleted();
    } else {
      history.push('/');
    }
  }, [history]);

  useEffect(() => {
    if (briefAdded) {
      closeWindow();
    }
  }, [briefAdded, closeWindow]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const c = queryParams.get('code');
    const s = queryParams.get('state');
    const error = queryParams.get('error');

    if (error === 'access_denied') {
      cancelSlackAuth();
      setFailed(true);
      return;
    }

    if (error) {
      reportError(new Error(error));
      setFailed(true);
      return;
    }

    if (!c || !s) {
      reportError(new Error('Missing "code" or "state".'));
      setFailed(true);
      return;
    }

    setCode(c);
    setState(s);
  }, [location.search, history, setCode, setState, setFailed, closeWindow]);

  useEffect(() => {
    if (code && state && session && session.token) {
      setIsAddingSlackToBrief(true);
      slackManager
        .addSlackChannel(code, state)
        .then(() => {
          // The opener refreshes the brief details when the popup completes,
          // so no cache update is needed from this window.
          setIsAddingSlackToBrief(false);
          setBriefAdded(true);
        })
        .catch(error => handleServiceError(error, () => setIsAddingSlackToBrief(false)));
    } else if (failed) {
      closeWindow();
    }
  }, [code, state, session, handleServiceError, closeWindow, failed]);

  if (failed || (isAddingSlackToBrief === false && briefAdded === undefined)) {
    return (
      <div>
        <Card>
          <Card.Body>
            <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-gray-900 mb-6">
              Oops, something went wrong
            </h1>
            <Form noValidate>
              <Button className="mt-4 w-full flex" as={Link} to={'/'}>
                Back
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Card>
        <Card.Body>
          <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-gray-900 mb-6">
            <Spinner animation="grow"></Spinner> Authenticating with Slack...
          </h1>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SlackCallbackPage;
